(() => {
  'use strict';

  const ARTICLE_CONTENT_SELECTOR = '.article-template__content';
  const CONTEXT_SELECTOR = '[data-blog-product-context]';
  const TRACKED_LINK_SELECTOR = 'a[data-blog-product-link="true"]';

  /*
   * Content writers mark an inline product CTA by setting:
   *
   * Link title = CTA
   *
   * in Shopify's normal blog article link editor.
   */
  const CTA_MARKER = 'cta';

  /*
   * Supports:
   * /products/product-handle
   * /fr/products/product-handle
   * /en-ro/products/product-handle
   * /collections/example/products/product-handle
   */
  const PRODUCT_PATH_PATTERN = /\/products\/([^/?#]+)/i;

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  /**
   * Validate that a URL:
   * - stays on the current storefront origin;
   * - points to a Shopify product URL.
   *
   * No URL is modified.
   */
  function getProductFromHref(href) {
    try {
      const url = new URL(href, window.location.href);

      if (url.origin !== window.location.origin) {
        return null;
      }

      const match = url.pathname.match(PRODUCT_PATH_PATTERN);

      if (!match) {
        return null;
      }

      return {
        handle: decodeURIComponent(match[1]),
        path: `${url.pathname}${url.search}`,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Enhance marked article links.
   */
  function enhanceContent(content) {
    content.querySelectorAll('a[title]').forEach((anchor) => {
      if (normalize(anchor.getAttribute('title')) !== CTA_MARKER) {
        return;
      }

      const product = getProductFromHref(anchor.getAttribute('href'));

      /*
       * A "CTA" marker pointing anywhere other than an internal
       * product URL is deliberately ignored.
       */
      if (!product) {
        return;
      }

      /*
       * Reuse Dawn's native button component.
       */
      anchor.classList.add(
        'button',
        'button--secondary',
        'blog-inline-product-cta'
      );

      /*
       * Tracking metadata.
       */
      anchor.dataset.blogProductLink = 'true';
      anchor.dataset.clickLocation = 'inline';
      anchor.dataset.linkElement = 'button';
      anchor.dataset.productHandle = product.handle;

      /*
       * "CTA" is only an authoring marker.
       * Don't leave it visible as a browser tooltip.
       */
      anchor.removeAttribute('title');

      /*
       * Give the CTA paragraph appropriate editorial spacing,
       * but only if the paragraph contains nothing except this link.
       */
      const paragraph = anchor.closest('p');

      if (
        paragraph &&
        paragraph.children.length === 1 &&
        paragraph.firstElementChild === anchor &&
        paragraph.textContent.trim() === anchor.textContent.trim()
      ) {
        paragraph.classList.add('blog-inline-product-cta-wrapper');
      }
    });
  }

  function enhanceInlineCtas(root = document) {
    if (
      root instanceof Element &&
      root.matches(ARTICLE_CONTENT_SELECTOR)
    ) {
      enhanceContent(root);
      return;
    }

    if (typeof root.querySelectorAll !== 'function') {
      return;
    }

    root
      .querySelectorAll(ARTICLE_CONTENT_SELECTOR)
      .forEach(enhanceContent);
  }

  /**
   * Push one blog_product_click event.
   *
   * Navigation is never blocked or delayed.
   */
  function trackBlogProductClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const link = event.target.closest(TRACKED_LINK_SELECTOR);

    if (!link) {
      return;
    }

    const product = getProductFromHref(link.getAttribute('href'));
    const context = document.querySelector(CONTEXT_SELECTOR);

    /*
     * Don't contaminate analytics while clicking inside
     * Shopify's Theme Editor.
     */
    if (!product || !context || context.dataset.designMode === 'true') {
      return;
    }

    /*
     * Recommended-product cards contain richer product data.
     * Inline links fall back to information parsed from the href.
     */
    const productContext =
      link.closest('[data-product-context]') || link;

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'blog_product_click',

      blog_click_location:
        link.dataset.clickLocation || '',

      blog_link_element:
        link.dataset.linkElement || '',

      blog_article_id:
        context.dataset.articleId || '',

      blog_article_handle:
        context.dataset.articleHandle || '',

      blog_article_title:
        context.dataset.articleTitle || '',

      blog_article_path:
        context.dataset.articleUrl || window.location.pathname,

      product_id:
        productContext.dataset.productId || '',

      product_handle:
        productContext.dataset.productHandle || product.handle,

      product_title:
        productContext.dataset.productTitle || '',

      product_path:
        product.path,

      storefront_language:
        context.dataset.language ||
        document.documentElement.lang ||
        '',

      storefront_country:
        context.dataset.country || '',

      storefront_market:
        context.dataset.market || '',
    });
  }

  /*
   * Initial enhancement.
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => enhanceInlineCtas()
    );
  } else {
    enhanceInlineCtas();
  }

  /*
   * One delegated listener for all inline and recommended links.
   * Capture phase lets the dataLayer push happen before navigation
   * without preventDefault(), delays or timeouts.
   */
  document.addEventListener(
    'click',
    trackBlogProductClick,
    true
  );

  /*
   * Shopify Theme Editor support.
   * No MutationObserver is required on the storefront.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      enhanceInlineCtas(event.target);
    }
  );
})();